import { dateId, excelDownloadId, pdfDownloadId, searchId } from "@/assets/googleTrackIds";
import { FiltersButton } from "@/components/Dataheader/FiltersButton";
import { FiltersDrawer } from "@/components/Dataheader/FiltersDrawer";
import { FC, useCallback, useEffect, useState } from "react";
import {
  Button,
  DataHeader,
  Dropdown,
  Flex,
  Input,
  LegacyDatePicker,
  LegacyForm,
  Space,
  Typography,
  usePaypingContext,
} from "@payping/web-components";
import { PhRDownloadSimple } from "@payping/icons";
import { submitQueryParams } from "@payping/utils/query-string";
import { SDKDateObject, dateFormats, toUtc } from "@payping/utils/dateUtils";
import { useLocation, useNavigate } from "react-router-dom";
import qs from "query-string";
import { WithdrawsFilters, WithdrawsStringFilters } from "@/utils/Sale/initialFilters";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { sdkFetch, uploadV2Apis } from "@payping/api";
import { DownloadTypes } from "./AppSubNav/Download";

interface Fields extends Omit<WithdrawsFilters, "fromDate" | "toDate"> {
  rangePicker?: SDKDateObject[];
}

interface Props {
  retry: () => void;
  loading: boolean;
}

export const WithdrawFilter: FC<Props> = (props) => {
  const { breakpoints } = usePaypingContext();
  const { xl } = breakpoints;
  const { retry } = props;
  const location = useLocation();
  // Because there are different types of filters, we should separate parsing them to avoid conflict between types (like converting "241e43" string to scientific number 2.41e45)
  const { clientId, fromDate, searchText, toDate }: WithdrawsStringFilters = qs.parse(location.search);

  // Because there are different types of filters, we should separate parsing them to avoid conflict between types (like converting "241e43" string to scientific number 2.41e45)
  const [openDrawer, setOpenDrawer] = useState(false);
  const { t } = useAppTranslation();
  const parsed: WithdrawsStringFilters = qs.parse(location.search);
  const navigate = useNavigate();
  const [form] = LegacyForm.useForm<Fields>();
  const backFormat = dateFormats.standard.utcDateTime;
  const [loading, setLoading] = useState(false); // Local state for loading

  const initialValues: Fields = {};

  const onFinish = useCallback(
    ({ rangePicker, ...selectedFilters }: Fields): void => {
      setLoading(true); // Start loading
      const [from, to] = rangePicker
        ? [rangePicker[0]?.format(backFormat), rangePicker[1]?.format(backFormat)]
        : [undefined, undefined];
      const newValues: WithdrawsFilters = {
        ...parsed,
        clientId: selectedFilters.clientId,
        fromDate: from,
        searchText: selectedFilters.searchText,
        toDate: to,
      };
      submitQueryParams({
        navigate,
        newValues,
        parsedSearch: parsed,
        pathname: location.pathname,
        retry,
      });

      setLoading(false); // Start loading
    },
    [backFormat, navigate, location.pathname, parsed, retry],
  );

  useEffect(() => {
    const initDateValues: SDKDateObject[] = [];
    if (fromDate) {
      initDateValues[0] = toUtc({
        convertTime: false,
        date: fromDate,
        dateFormat: dateFormats.standard.utcDateTime,
      }).object as SDKDateObject;
    }
    if (toDate) {
      initDateValues[1] = toUtc({
        convertTime: false,
        date: toDate,
        dateFormat: dateFormats.standard.utcDateTime,
      }).object as SDKDateObject;
    }
    const valuesToSet: Fields = {
      clientId,
      rangePicker: initDateValues,
    };
    form.setFieldsValue(valuesToSet);
  }, [clientId, form, fromDate, toDate]);

  const handleDownload = useCallback(
    async (type: DownloadTypes, newWindow: Window | null): Promise<void> => {
      const body = { clientId, fromDate, searchText, toDate };
      let url;
      if (type === "Excel") {
        const response = await sdkFetch("/v1/upload/WithdrawsExcelExport", {
          body: JSON.stringify(body),
          method: "POST",
        });
        url = await response.text();
      } else {
        url = await uploadV2Apis.withdrawsPdfExportWithModelPost(body);
      }
      newWindow?.location?.assign?.(url);
    },
    [clientId, fromDate, searchText, toDate],
  );

  const items = [
    {
      key: "1",
      label: <span data-google-track={excelDownloadId}>{t("downloadExcel")}</span>,
      onClick: () => {
        const newWindow = window.open(undefined, "_blank");
        handleDownload("Excel", newWindow);
      },
    },
    {
      key: "2",
      label: <span data-google-track={pdfDownloadId}>{t("downloadPdf")}</span>,
      onClick: () => {
        const newWindow = window.open(undefined, "_blank");
        handleDownload("PDF", newWindow);
      },
    },
  ];

  return (
    <LegacyForm<Fields> form={form} onFinish={onFinish} initialValues={initialValues}>
      <DataHeader
        title={
          <Typography.Title className={"tw:m-0!"} level={4}>
            {t("report.tabs.withdraws")}
          </Typography.Title>
        }
        filters={{
          datepickerInput: (
            <LegacyForm.Item key={1} name={"rangePicker"} noStyle>
              <LegacyDatePicker.RangePicker
                className="w-100"
                onChange={() => form.submit()}
                data-google-track={dateId}
              />
            </LegacyForm.Item>
          ),
          searchbarInput: xl && (
            <Space.Compact key={2} className="tw:w-full">
              <LegacyForm.Item name={"searchText"} noStyle>
                <Input.Search
                  allowClear
                  placeholder={t("report.table.filter.search.placeholder")}
                  onSearch={() => form.submit()}
                  data-google-track={searchId}
                />
              </LegacyForm.Item>
            </Space.Compact>
          ),
          downloadButton: (
            <LegacyForm.Item key={4} noStyle>
              <Dropdown
                menu={{
                  items,
                }}
              >
                <Button block icon={<PhRDownloadSimple width={14} height={14} />}>
                  {t("downloadFile")}
                </Button>
              </Dropdown>
            </LegacyForm.Item>
          ),
          filtersButton: !xl && (
            <>
              {/* < آیکن و دکمه فیلتر ها > */}
              <FiltersButton onClick={() => setOpenDrawer(true)} params={{ clientId, searchText, toDate }} />

              {/* < دراور فیلترها > */}
              <FiltersDrawer
                title={t("filter")}
                onClose={() => setOpenDrawer(false)}
                onClickResetFilters={() => {
                  form.resetFields();
                  navigate({ pathname: location.pathname });
                  setOpenDrawer(false);
                }}
                onClickApplyFilters={() => {
                  form.submit();
                  setOpenDrawer(false);
                }}
                open={openDrawer}
                loading={loading}
              >
                <Flex vertical gap={8}>
                  {/* < SearchBar > */}
                  <LegacyForm.Item label={t("report.search")} name={"searchText"} layout={"vertical"}>
                    <Input placeholder={t("report.table.filter.search.placeholder")} data-google-track={searchId} />
                  </LegacyForm.Item>
                </Flex>
              </FiltersDrawer>
            </>
          ),
        }}
      />
    </LegacyForm>
  );
};

export default WithdrawFilter;
